import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DEFAULT_BATCH_REPEATS, Estimate, TargetInfo, TestRun, TestsApi, fmtCredits } from '../../services/testsApi';
import { ModelChecklist, modelLabel } from './common';
import { errorText } from './dialogs';

export const REPEAT_OPTIONS = [1, 2, 3, 5];

/** Pick models and repeats for one function or chain, see the cost, start the run. */
export const RunPanelDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  target: TargetInfo;
  caseCount: number;
  needsVision?: boolean;
  onClose: () => void;
  onStarted: (run: TestRun) => void;
}> = ({ open, api, target, caseCount, needsVision, onClose, onStarted }) => {
  const [models, setModels] = useState<string[]>(['default']);
  const [repeats, setRepeats] = useState(1);
  const [steps, setSteps] = useState<string[]>([]);
  const [cap, setCap] = useState('');
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chain = target.target_type === 'chain';

  useEffect(() => {
    if (!open) return;
    setModels(['default']);
    setRepeats(1);
    setSteps((target.steps || []).map(s => s.node_id));
    setCap('');
    setError(null);
  }, [open, target]);

  // Comparing models: repeats show which models are consistent, so default to a few.
  const comparing = models.filter(m => m !== 'default').length > 0;
  useEffect(() => {
    setRepeats(r => (comparing && r === 1 ? DEFAULT_BATCH_REPEATS : !comparing && r === DEFAULT_BATCH_REPEATS ? 1 : r));
  }, [comparing]);

  const config = { models, repeats, ...(chain ? { chain_steps: steps } : {}) };
  const configKey = JSON.stringify(config);

  useEffect(() => {
    if (!open || !models.length) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .estimate(target.target_type, target.target_id, JSON.parse(configKey))
        .then(e => {
          if (cancelled) return;
          setEstimate(e);
          setEstimateError(null);
        })
        .catch(e => {
          if (cancelled) return;
          setEstimate(null);
          setEstimateError(errorText(e));
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [api, open, target, configKey, models.length]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const capValue = Number(cap);
      const run = await api.startRun(target.target_type, target.target_id, config, cap && capValue > 0 ? capValue : null);
      onStarted(run);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Run tests on {target.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Models
            </Typography>
            <ModelChecklist
              value={models}
              onChange={setModels}
              context={{ defaultModel: target.default_model, allowedModels: target.allowed_models, needsVision }}
              api={api}
            />
            {!chain && target.allowed_models && models.some(m => m !== 'default' && !target.allowed_models?.includes(m)) && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Tests can try any model, including ones this function doesn't allow yet.
              </Typography>
            )}
          </Box>
          {chain && (target.steps || []).length > 0 && (
            <Box>
              <Typography variant="subtitle2">Steps that switch model</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                The others keep their own model. "Current default" leaves every step as it is.
              </Typography>
              {(target.steps || []).map(s => (
                <FormControlLabel
                  key={s.node_id}
                  sx={{ display: 'flex' }}
                  control={
                    <Checkbox
                      size="small"
                      checked={steps.includes(s.node_id)}
                      onChange={() => setSteps(v => (v.includes(s.node_id) ? v.filter(x => x !== s.node_id) : [...v, s.node_id]))}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {s.label || s.function_name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        now {modelLabel(s.model)}
                      </Typography>
                    </Typography>
                  }
                />
              ))}
            </Box>
          )}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Runs per case
              </Typography>
              <Select size="small" value={repeats} onChange={e => setRepeats(Number(e.target.value))} sx={{ minWidth: 100 }}>
                {REPEAT_OPTIONS.map(n => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, maxWidth: 220 }}>
                {DEFAULT_BATCH_REPEATS} is a good balance when comparing models: it shows which ones are consistent.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Credit cap (optional)
              </Typography>
              <TextField size="small" type="number" value={cap} onChange={e => setCap(e.target.value)} placeholder="Automatic" sx={{ width: 140 }} />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, maxWidth: 220 }}>
                Stops the run when reached. Automatic is twice the estimate. A stopped run can be finished later.
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
            {estimateError ? (
              <Typography variant="body2" color="error">
                {estimateError}
              </Typography>
            ) : estimate ? (
              <>
                <Typography variant="body2">
                  {estimate.cases} case{estimate.cases === 1 ? '' : 's'} × {estimate.models.length} model{estimate.models.length === 1 ? '' : 's'} ×{' '}
                  {estimate.repeats} = <b>{estimate.calls}</b> calls · about <b>{fmtCredits(estimate.credits)}</b> credits
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {fmtCredits(estimate.run_credits)} for the runs, {fmtCredits(estimate.judge_credits)} for grading. Billed like normal runs and
                  labelled "Tests"; they don't count toward your stats or alerts.
                  {estimate.graded_cases < estimate.cases &&
                    ` ${estimate.cases - estimate.graded_cases} case${estimate.cases - estimate.graded_cases === 1 ? " has" : "s have"} no expected output, so ${estimate.cases - estimate.graded_cases === 1 ? "it's" : "they're"} only checked for errors and speed.`}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {caseCount ? 'Working out the cost…' : 'Enable some test cases first.'}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={start}
          disabled={starting || !models.length || !estimate || !estimate.cases || (chain && comparing && !steps.length)}
        >
          {starting ? 'Starting…' : 'Start'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
